import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { ReactReduxContext } from "react-redux";
import type { HotelAvailabilityTrackingProperties } from "@b2bportal/core-tracking";
import type {
  EventTransformerHooks,
  UseInstantTransformEvent,
  GlobalTrackingAttributes,
} from "@b2bportal/core-tracking";
import { useEventTracking } from "@b2bportal/core-tracking";
import { getSearchFormPackageType } from "../features/search/store/selectors";
import {
  type PackageEntry,
  type PackageViewedSearch,
  type PackageHotelListLoaded,
  type PackageHotelUpdatedListPage,
  type PackageHotelCoverViewed,
  type PackageSelectedFlightOutbound,
  type PackageSelectedFlightReturn,
  type PackageReviewDetails,
  PackageCommonTrackingEventsName,
  PackageEventName,
  PACKAGE_PRODUCT_TYPE,
} from "./events";
import {
  getPackageHotelAvailabilityTrackingProperties,
  getPackageHotelShopTrackingProperties,
  getPackageHotelShopAvailabilityTrackingProperties,
  getPackageFlightSelectionTrackingProperties,
  getPackageReviewDetailsTrackingProperties,
  type CombinedPackageHotelsState,
} from "./selectors";
import {
  type PackageAppLoadedRawEvent,
  type PackageSearchLoadedRawEvent,
  type PackageHotelAvailabilityPageLoadedRawEvent,
  type PackageHotelUpdatedListPageRawEvent,
  type PackageHotelShopPageLoadedRawEvent,
  type PackageSelectedFlightOutboundRawEvent,
  type PackageSelectedFlightReturnRawEvent,
  type PackageReviewDetailsLoadedRawEvent,
  type PackagesRawEvent,
  PackagesRawEventName,
} from "./types";

/**
 * Common properties shared across all package tracking events.
 * Includes productType and package_type from Redux state.
 */
export const getCommonProperties = (state: CombinedPackageHotelsState) => ({
  productType: PACKAGE_PRODUCT_TYPE,
  package_type: getSearchFormPackageType(state),
});

export const trackOnlyOncePerSession = (event: PackageEntry): boolean => {
  const sessionStorageKey = `package_event_${event.name}`;
  if (sessionStorage.getItem(sessionStorageKey)) {
    return false;
  }
  sessionStorage.setItem(sessionStorageKey, "true");
  return true;
};

export const usePackageAppLoadedTransformer: UseInstantTransformEvent<
  PackageAppLoadedRawEvent,
  PackageEntry
> = () => {
  const packageType = useSelector(getSearchFormPackageType);

  return useCallback(
    (event) => {
      const name: PackageCommonTrackingEventsName.package_entry =
        PackageCommonTrackingEventsName.package_entry;
      const htsEvent = {
        name,
        properties: {
          entry: event.properties.entry,
          productType: PACKAGE_PRODUCT_TYPE,
          package_type: packageType,
        },
      };

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [packageType]
  );
};

export const usePackageSearchLoadedTransformer: UseInstantTransformEvent<
  PackageSearchLoadedRawEvent,
  PackageViewedSearch
> = () => {
  const packageType = useSelector(getSearchFormPackageType);

  return useCallback(() => {
    const name = PackageCommonTrackingEventsName.package_viewed_search;
    const htsEvent: PackageViewedSearch = {
      name,
      properties: {
        productType: PACKAGE_PRODUCT_TYPE,
        package_type: packageType,
      },
    };

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [packageType]);
};

export const shouldTrackEvent = (
  event: PackageEntry | PackageViewedSearch
): boolean => {
  if (event.name === PackageCommonTrackingEventsName.package_entry) {
    return trackOnlyOncePerSession(event as PackageEntry);
  }
  return true;
};

export const useGetProviders = () => {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(
    () => ({
      trackingAttributes: getAllAttributes(),
      reduxState: reduxContext.store.getState(),
    }),
    [reduxContext.store, getAllAttributes]
  );
};

export interface Providers {
  trackingAttributes: GlobalTrackingAttributes;
  reduxState: CombinedPackageHotelsState;
}

export const defaultAvailabilityPageEventTransformer = <
  T extends PackageHotelListLoaded | PackageHotelUpdatedListPage,
>(
  eventName: T["name"],
  providers: Providers,
  extraProperties: Omit<
    T["properties"],
    | keyof HotelAvailabilityTrackingProperties["properties"]
    | keyof ReturnType<typeof getCommonProperties>
  >
) => {
  const { properties, encryptedProperties } =
    getPackageHotelAvailabilityTrackingProperties(providers.reduxState);
  return {
    name: eventName,
    properties: {
      ...properties,
      ...extraProperties,
      ...getCommonProperties(providers.reduxState),
    },
    encryptedProperties: encryptedProperties ?? [],
  };
};

export const defaultShopPageEventTransformer = <
  T extends PackageHotelCoverViewed,
>(
  eventName: T["name"],
  providers: Providers,
  extraProperties: Omit<
    T["properties"],
    | keyof ReturnType<
        typeof getPackageHotelShopAvailabilityTrackingProperties
      >["properties"]
    | keyof ReturnType<
        typeof getPackageHotelShopTrackingProperties
      >["properties"]
    | keyof ReturnType<typeof getCommonProperties>
  >
): {
  name: T["name"];
  properties: T["properties"];
  encryptedProperties: string[];
} => {
  const {
    properties: shopAvailabilityProperties,
    encryptedProperties: availabilityEncryptedTrackingProperties,
  } = getPackageHotelShopAvailabilityTrackingProperties(providers.reduxState);
  const { properties: shopProperties, encryptedProperties } =
    getPackageHotelShopTrackingProperties(providers.reduxState);

  return {
    name: eventName,
    properties: {
      ...shopAvailabilityProperties,
      ...shopProperties,
      ...getCommonProperties(providers.reduxState),
      ...extraProperties,
    },
    encryptedProperties: [
      availabilityEncryptedTrackingProperties ?? "",
      encryptedProperties ?? "",
    ].filter(Boolean),
  };
};

export const usePackageHotelAvailabilityPageLoadedTransformer: UseInstantTransformEvent<
  PackageHotelAvailabilityPageLoadedRawEvent,
  PackageHotelListLoaded
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultAvailabilityPageEventTransformer<PackageHotelListLoaded>(
          PackageEventName.package_hotel_loaded_list_page,
          getProviders(),
          {
            page_index: event.properties?.pageIndex ?? 0,
            page_property_count: event.properties?.pagePropertyCount ?? 0,
          }
        );

      return htsEvent;
    },
    [getProviders]
  );
};

export const usePackageHotelUpdatedListPageTransformer: UseInstantTransformEvent<
  PackageHotelUpdatedListPageRawEvent,
  PackageHotelUpdatedListPage
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultAvailabilityPageEventTransformer<PackageHotelUpdatedListPage>(
          PackageEventName.package_hotel_updated_list_page,
          getProviders(),
          {
            page_index: event.properties?.pageIndex ?? 0,
            page_property_count: event.properties?.pagePropertyCount ?? 0,
          }
        );

      return htsEvent;
    },
    [getProviders]
  );
};

export const usePackageHotelShopPageLoadedTransformer: UseInstantTransformEvent<
  PackageHotelShopPageLoadedRawEvent,
  PackageHotelCoverViewed
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultShopPageEventTransformer<PackageHotelCoverViewed>(
      PackageEventName.package_hotel_viewed_cover,
      getProviders(),
      {}
    );

    return htsEvent;
  }, [getProviders]);
};

export const usePackageSelectedFlightOutboundTransformer: UseInstantTransformEvent<
  PackageSelectedFlightOutboundRawEvent,
  PackageSelectedFlightOutbound
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const providers = getProviders();
      const { properties } = getPackageFlightSelectionTrackingProperties(
        providers.reduxState,
        event.properties
      );

      const htsEvent: PackageSelectedFlightOutbound = {
        name: PackageEventName.package_selected_flight_outbound,
        properties: {
          ...properties,
          flow_type: (providers.trackingAttributes as Record<string, unknown>)
            .flow_type as string | undefined,
          ...getCommonProperties(providers.reduxState),
        },
      };

      return htsEvent;
    },
    [getProviders]
  );
};

export const usePackageSelectedFlightReturnTransformer: UseInstantTransformEvent<
  PackageSelectedFlightReturnRawEvent,
  PackageSelectedFlightReturn
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const providers = getProviders();
      const { properties } = getPackageFlightSelectionTrackingProperties(
        providers.reduxState,
        event.properties
      );

      const htsEvent: PackageSelectedFlightReturn = {
        name: PackageEventName.package_selected_flight_return,
        properties: {
          ...properties,
          flow_type: (providers.trackingAttributes as Record<string, unknown>)
            .flow_type as string | undefined,
          ...getCommonProperties(providers.reduxState),
        },
      };

      return htsEvent;
    },
    [getProviders]
  );
};

export const usePackageReviewDetailsLoadedTransformer: UseInstantTransformEvent<
  PackageReviewDetailsLoadedRawEvent,
  PackageReviewDetails
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const providers = getProviders();
      const { properties, encryptedProperties } =
        getPackageReviewDetailsTrackingProperties(
          providers.reduxState,
          event.properties
        );

      const htsEvent: PackageReviewDetails = {
        name: PackageEventName.package_review_details,
        properties: {
          ...properties,
          flow_type: (providers.trackingAttributes as Record<string, unknown>)
            .flow_type as string | undefined,
          ...getCommonProperties(providers.reduxState),
        },
        encryptedProperties:
          encryptedProperties != null && encryptedProperties.length > 0
            ? [encryptedProperties]
            : [],
      };

      return htsEvent;
    },
    [getProviders]
  );
};

export const PACKAGE_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  PackagesRawEvent,
  | PackageEntry
  | PackageViewedSearch
  | PackageHotelListLoaded
  | PackageHotelUpdatedListPage
  | PackageHotelCoverViewed
  | PackageSelectedFlightOutbound
  | PackageSelectedFlightReturn
  | PackageReviewDetails
> = {
  [PackagesRawEventName.PackageAppLoaded]: [usePackageAppLoadedTransformer],
  [PackagesRawEventName.PackageSearchLoaded]: [
    usePackageSearchLoadedTransformer,
  ],
  [PackagesRawEventName.PackageHotelAvailabilityPageLoaded]: [
    usePackageHotelAvailabilityPageLoadedTransformer,
  ],
  [PackagesRawEventName.PackageHotelUpdatedListPage]: [
    usePackageHotelUpdatedListPageTransformer,
  ],
  [PackagesRawEventName.PackageHotelShopPageLoaded]: [
    usePackageHotelShopPageLoadedTransformer,
  ],
  [PackagesRawEventName.PackageSelectedFlightOutbound]: [
    usePackageSelectedFlightOutboundTransformer,
  ],
  [PackagesRawEventName.PackageSelectedFlightReturn]: [
    usePackageSelectedFlightReturnTransformer,
  ],
  [PackagesRawEventName.PackageReviewDetailsLoaded]: [
    usePackageReviewDetailsLoadedTransformer,
  ],
};
